import React, { useEffect, useState, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import CustomText from './custom-text.atom';

const CustomTextInput = forwardRef(({
  label = '',
  error = '',
  helper = '',
  value = '',
  type = 'text',
  maxLength = null,
  onChange = (e) => {},
  onPress = undefined,
  onBlur,
  placeholder,
  onFocusEvent = undefined,
  right = null,
  containerStyle,
  inputStyle,
  inputContainerStyle,
  disabled = false,
  ...additionalInputProps
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isLabelFloating, setIsLabelFloating] = useState(!!value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!value && !isFocused) {
      setIsLabelFloating(false);
    } else if (value) {
      setIsLabelFloating(true);
    }
  }, [value, isFocused]);

  React.useImperativeHandle(ref, () => ({
    focus: () => {
      handleFocus();
    }
  }));

  const handleFocus = () => {
    setIsFocused(true);
    if (onFocusEvent) {
      onFocusEvent();
    }
    if (onPress) onPress();
    setIsLabelFloating(true);
    inputRef.current?.focus();
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (onBlur) {
      onBlur();
    }
    if (!value) {
      setIsLabelFloating(false);
    }
  };

  return (
    <div style={{ ...styles.baseContainer, ...containerStyle }} onClick={handleFocus}>
      <div
        style={{
          ...styles.inputContainer,
          ...inputContainerStyle,
          ...(isFocused && styles.focusedInput),
          ...(error && styles.errorInput),
          ...(disabled && styles.disabledInput),
        }}
      >
        <div style={{ flex: 1, display: 'flex', flexDirection: 'row' }}>
          <input
            ref={inputRef}
            type={type}
            maxLength={maxLength}
            style={{ ...styles.input, ...inputStyle, ...(disabled && styles.disabledText) }}
            value={value}
            placeholder={label && !isFocused ? '' : placeholder}
            onChange={(e) => onChange(e.target.value)}
            onBlur={handleBlur}
            disabled={disabled}
            {...additionalInputProps}
          />
          {right !== null && (
            <div style={styles.rightContainer}>{right()}</div>
          )}
        </div>
        {label && (
          <div
            style={{
              ...styles.labelContainer,
              ...(disabled && styles.labelDisabled),
              top: isLabelFloating ? '-12px' : '16px',
            }}
          >
            <div style={styles.labelBackground} />
            <CustomText
              variant="body"
              style={{
                ...styles.label,
                fontSize: isLabelFloating ? '12px' : '16px',
                color: error
                  ? styles.error.color
                  : isFocused
                    ? styles.focusedLabel.color
                    : styles.label.color,
                fontWeight: isFocused ? '300' : '400',
              }}
            >
              {label}
            </CustomText>
          </div>
        )}
      </div>
      {(error || helper) && (
        <CustomText
          variant="caption"
          style={error ? styles.errorText : styles.helperText}
        >
          {error || helper}
        </CustomText>
      )}
    </div>
  );
});

CustomTextInput.propTypes = {
  label: PropTypes.string,
  type: PropTypes.oneOf([
    'text',
    'email',
    'number',
    'tel',
    'url',
    'search',
    'password'
  ]),
  error: PropTypes.string,
  value: PropTypes.string,
  helper: PropTypes.string,
  onChange: PropTypes.func,
  containerStyle: PropTypes.object,
  inputStyle: PropTypes.object,
  onPress: PropTypes.func,
  onFocus: PropTypes.func,
  right: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  inputContainerStyle: PropTypes.object,
};

const styles = {
  baseContainer: {
    paddingTop: '8px',
  },
  inputContainer: {
    position: 'relative',
    border: 'solid',
    borderColor: '#626262',
    borderWidth: '1px',
    borderRadius: '8px',
    paddingLeft: '12px',
    paddingRight: '12px',
    backgroundColor: 'transparent',
  },
  input: {
    flex: 1,
    height: '54px',
    fontSize: '16px',
    paddingLeft: '8px',
    paddingRight: '8px',
    lineHeight: '22px',
    letterSpacing: '0.2px',
    fontFamily: 'Inter, sans-serif',
    color: '#000',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    width: '100%',
    overflow: 'hidden',
  },
  labelContainer: {
    position: 'absolute',
    zIndex: 2,
    left: '10px',
    transition: 'all 0.2s ease-out',
  },
  labelDisabled: {
    color: '#909090',
  },
  labelBackground: {
    position: 'absolute',
    top: '8px',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#FFFFFF',
  },
  label: {
    padding: '0 8px',
    transition: 'all 0.2s ease-out',
    backgroundColor: '#FFFFFF',
    position: 'relative',
  },
  rightContainer: {
    width: '8%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  focusedLabel: {
    color: '#FACE07',
  },
  error: {
    color: '#87003B',
  },
  errorText: {
    color: '#87003B',
    marginTop: '4px',
    marginLeft: '12px',
    marginRight: '12px',
  },
  helperText: {
    color: '#626262',
    marginTop: '4px',
    marginLeft: '12px',
    marginRight: '12px',
  },
  focusedInput: {
    borderColor: '#FACE07',
    borderWidth: '2px',
  },
  errorInput: {
    borderColor: '#87003B',
    borderWidth: '2px',
  },
  disabledInput: {
    borderColor: '#909090',
    borderWidth: '1px',
  },
  disabledText: {
    color: '#909090',
  },
  '&::placeholder': {
    color: '#909090',
  },
};

export default CustomTextInput;